/* istanbul ignore file */

import { type useLastInteractedStore } from '../../../../context/LastInteractedStore/useLastInteractedStore';
import { useLastInteractedStoreMachine } from '../../../../context/LastInteractedStore/useLastInteractedStoreMachine';
import { type useReorderOrderLineItem } from '../../useReorderOrderLineItem';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The location used for reorder should follow this list of priority:
 * 1. Explicit target restaurant (e.g. the menu reorder rail always reorders
 *    into the restaurant whose menu is being viewed).
 * 2. Active Cart (from last interacted store machine).
 * 3. Last menu (from last interacted store machine).
 * 4. Most recent order (from the reorder query on the screen).
 *
 * Note: We are not using the last order from the last interacted store machine.
 * Instead we use the already provided most recent order, to avoid another query.
 * This is because `useLastValidOrder` is cache-only and is only queried from `OrderProductConfirmProductLoaded`.
 */
export const useReorderOrderLineItemLocation = (params: UseReorderOrderLineItemParams) => {
  const { mostRecentOrder, targetRestaurant } = params;

  const [state] = useLastInteractedStoreMachine();

  // ─── Explicit Target Restaurant ──────────────────────────────────────

  if (targetRestaurant) {
    return {
      restaurantId: targetRestaurant.restaurantId,
      restaurantName: targetRestaurant.restaurantName,
      restaurantSlug: targetRestaurant.restaurantSlug,
      deliveryOrderDetails: targetRestaurant.deliveryOrderDetails,
    };
  }

  const { interactedStores: stores } = state.context;
  const lastInteractedStore = stores.activeCart || stores.menu;

  // ─── Last Interacted Store ───────────────────────────────────────────

  if (lastInteractedStore) {
    return {
      restaurantId: lastInteractedStore.restaurantId,
      restaurantName: lastInteractedStore.restaurantName,
      restaurantSlug: lastInteractedStore.restaurantSlug,
      deliveryOrderDetails: getDeliveryOrderDetails(lastInteractedStore),
    };
  }

  // ─── Most Recent Order ───────────────────────────────────────────────

  return {
    restaurantId: mostRecentOrder?.restaurant?.id,
    restaurantName: mostRecentOrder?.restaurant?.slug,
    restaurantSlug: mostRecentOrder?.restaurant?.name,
    deliveryOrderDetails: mostRecentOrder?.deliveryOrderDetail,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getDeliveryOrderDetails = (order?: LastInteractedStore) => {
  if (!order?.addressId || !order?.vendor || !order?.vendorRestaurantId) {
    return undefined;
  }

  return {
    addressId: order.addressId,
    tip: 0,
    deliveryFee: order.deliveryFee ?? 0,
    vendor: order.vendor,
    vendorRestaurantId: order.vendorRestaurantId,
    address: { id: order.addressId },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LastInteractedStore = ReturnType<typeof useLastInteractedStore>;

type UseReorderOrderLineItemParams = Pick<
  Parameters<typeof useReorderOrderLineItem>[0],
  'mostRecentOrder' | 'targetRestaurant'
>;
